import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  User,
  Buildings,
  Upload,
  Trash,
  Globe,
  SpinnerGap,
  CheckCircle,
  Camera,
  Image as ImageIcon,
  PencilSimple,
  Power,
  Plus,
  MagnifyingGlass,
  LockKey,
} from '@phosphor-icons/react';
import api from '../../services/api';
import { toast } from 'sonner';
import InfoTip from '../../components/InfoTip';

export default function Settings() {
  const { user, currentEvent, refreshUser, refreshCurrentEvent, isEventManager } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profileEmail, setProfileEmail] = useState(user?.email || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [companyName, setCompanyName] = useState(user?.company_name || '');
  const [companyWebsite, setCompanyWebsite] = useState(user?.company_website || '');
  const [previewLogo, setPreviewLogo] = useState(user?.company_logo || null);
  const fileInputRef = useRef(null);
  const bulkTeamMembersFileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [masterTeamMembers, setMasterTeamMembers] = useState([]);
  const [masterTeamMembersLoading, setMasterTeamMembersLoading] = useState(false);
  const [editingMasterTeamMember, setEditingMasterTeamMember] = useState(null);
  const [editingMasterTeamMemberForm, setEditingMasterTeamMemberForm] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [savingMasterTeamMember, setSavingMasterTeamMember] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [savingTeamMember, setSavingTeamMember] = useState(false);
  const [newTeamMember, setNewTeamMember] = useState({
    master_team_member_id: '',
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [newMasterTeamMember, setNewMasterTeamMember] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [savingMasterTeamMemberCreate, setSavingMasterTeamMemberCreate] = useState(false);
  const [downloadingMasterTemplate, setDownloadingMasterTemplate] = useState(false);
  const [uploadingMasterTeamMembers, setUploadingMasterTeamMembers] = useState(false);
  const [teamMembersTab, setTeamMembersTab] = useState('event');
  const [teamMemberSearch, setTeamMemberSearch] = useState('');
  const [teamMemberFilter, setTeamMemberFilter] = useState('all');
  const [addTeamMemberDialogOpen, setAddTeamMemberDialogOpen] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [selectedBulkTeamMembersFile, setSelectedBulkTeamMembersFile] = useState(null);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  useEffect(() => {
    setProfileName(user?.name || '');
    setProfileEmail(user?.email || '');
    setCompanyName(user?.company_name || '');
    setCompanyWebsite(user?.company_website || '');
    setPreviewLogo(user?.company_logo || null);
  }, [user?.name, user?.email, user?.company_name, user?.company_website, user?.company_logo]);

  useEffect(() => {
    if (isEventManager && currentEvent?.id) {
      fetchTeamMembers();
    }
  }, [isEventManager, currentEvent?.id]);

  useEffect(() => {
    if (isEventManager) {
      fetchMasterTeamMembers();
    }
  }, [isEventManager]);

  const fetchTeamMembers = async () => {
    if (!currentEvent?.id) return;
    try {
      setTeamMembersLoading(true);
      const response = await api.get(`/events/${currentEvent.id}/team-members`);
      setTeamMembers(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load team members');
    } finally {
      setTeamMembersLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setSavingProfile(true);
      await api.put('/auth/profile', {
        name: profileName,
        email: profileEmail || null,
      });
      toast.success('Profile updated');
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCreatePassword = async (event) => {
    event.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setSavingPassword(true);
      await api.post('/auth/password', { new_password: newPassword });
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password created. You can now use password login.');
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create password');
    } finally {
      setSavingPassword(false);
    }
  };

  const fetchMasterTeamMembers = async () => {
    if (!isEventManager) return;
    try {
      setMasterTeamMembersLoading(true);
      const response = await api.get('/team-member-masters');
      setMasterTeamMembers(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load reusable team members');
    } finally {
      setMasterTeamMembersLoading(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreviewLogo(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveBranding = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('company_name', companyName);
      formData.append('company_website', companyWebsite);
      if (selectedFile) {
        formData.append('logo', selectedFile);
      }
      
      const response = await api.put('/auth/company-branding', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Company branding updated!');
      setSelectedFile(null);
      if (response.data?.company_logo) {
        setPreviewLogo(response.data.company_logo);
      }
      if (refreshUser) await refreshUser();
      if (refreshCurrentEvent) await refreshCurrentEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update branding');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await api.delete('/auth/company-branding/logo');
      setPreviewLogo(null);
      toast.success('Company logo removed');
      if (refreshUser) await refreshUser();
      if (refreshCurrentEvent) await refreshCurrentEvent();
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const handleAddTeamMember = async (e) => {
    e.preventDefault();
    if (!currentEvent?.id) {
      toast.error('Please select an event first');
      return;
    }
    if (!newTeamMember.master_team_member_id && !newTeamMember.password) {
      toast.error('Password is required for a new team member');
      return;
    }

    try {
      setSavingTeamMember(true);
      await api.post(`/events/${currentEvent.id}/team-members`, {
        master_team_member_id: newTeamMember.master_team_member_id || null,
        name: newTeamMember.name,
        email: newTeamMember.email,
        phone: newTeamMember.phone,
        password: newTeamMember.master_team_member_id ? null : newTeamMember.password
      });
      toast.success('Team member added');
      setNewTeamMember({ master_team_member_id: '', name: '', email: '', phone: '', password: '' });
      setAddTeamMemberDialogOpen(false);
      fetchTeamMembers();
      fetchMasterTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    } finally {
      setSavingTeamMember(false);
    }
  };

  const isMasterMemberInCurrentEvent = (member) => {
    return assignedMasterMemberIds.has(member.id) || assignedMasterUserIds.has(member.user_id);
  };

  const handleAddMasterTeamMember = async (e) => {
    e.preventDefault();
    try {
      setSavingMasterTeamMemberCreate(true);
      await api.post('/team-member-masters', {
        name: newMasterTeamMember.name,
        email: newMasterTeamMember.email || null,
        phone: newMasterTeamMember.phone,
        password: newMasterTeamMember.password
      });
      toast.success('Team member saved to master list');
      setNewMasterTeamMember({ name: '', email: '', phone: '', password: '' });
      setAddTeamMemberDialogOpen(false);
      fetchMasterTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save team member');
    } finally {
      setSavingMasterTeamMemberCreate(false);
    }
  };

  const downloadMasterTeamTemplate = async () => {
    try {
      setDownloadingMasterTemplate(true);
      const response = await api.get('/team-member-masters/bulk-template', {
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      );
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', 'team_member_master_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download template');
    } finally {
      setDownloadingMasterTemplate(false);
    }
  };

  const handleBulkTeamMemberFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedBulkTeamMembersFile(file);
    setBulkUploadResult(null);
    event.target.value = '';
  };

  const handleBulkMasterTeamMemberUpload = async () => {
    if (!selectedBulkTeamMembersFile) {
      toast.error('Choose a spreadsheet first');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedBulkTeamMembersFile);

    try {
      setUploadingMasterTeamMembers(true);
      const response = await api.post('/team-member-masters/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const result = response.data || {};
      setBulkUploadResult(result);
      toast.success(result.message || 'Team members imported');
      if (result.skipped_count) {
        toast.info(`${result.skipped_count} row(s) skipped`);
      }
      setSelectedBulkTeamMembersFile(null);
      fetchMasterTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload team members');
    } finally {
      setUploadingMasterTeamMembers(false);
    }
  };

  const handleSelectSavedTeamMember = (masterTeamMemberId) => {
    if (!masterTeamMemberId) {
      setNewTeamMember({ master_team_member_id: '', name: '', email: '', phone: '', password: '' });
      return;
    }

    const savedMember = masterTeamMembers.find((member) => member.id === masterTeamMemberId);
    if (!savedMember) return;

    setNewTeamMember({
      master_team_member_id: savedMember.id,
      name: savedMember.name || '',
      email: savedMember.email || '',
      phone: savedMember.phone || '',
      password: ''
    });
  };

  const handleToggleTeamMemberStatus = async (member, source = 'event') => {
    const nextStatus = !member.is_active;
    try {
      if (source === 'master') {
        await api.put(`/team-member-masters/${member.id}/status`, { is_active: nextStatus });
      } else {
        await api.put(`/events/${currentEvent.id}/team-members/${member.id}/status`, { is_active: nextStatus });
      }
      toast.success(nextStatus ? 'Team member enabled' : 'Team member disabled');
      fetchTeamMembers();
      fetchMasterTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team member status');
    }
  };

  const handleOpenEditMasterTeamMember = (member) => {
    setEditingMasterTeamMember(member);
    setEditingMasterTeamMemberForm({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
    });
  };

  const handleSaveMasterTeamMember = async (e) => {
    e.preventDefault();
    if (!editingMasterTeamMember) return;

    try {
      setSavingMasterTeamMember(true);
      await api.put(`/team-member-masters/${editingMasterTeamMember.id}`, {
        name: editingMasterTeamMemberForm.name,
        email: editingMasterTeamMemberForm.email || null,
        phone: editingMasterTeamMemberForm.phone,
      });
      toast.success('Team member updated');
      setEditingMasterTeamMember(null);
      fetchTeamMembers();
      fetchMasterTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update reusable team member');
    } finally {
      setSavingMasterTeamMember(false);
    }
  };

  const handleAddMasterTeamMemberToCurrentEvent = async (member) => {
    if (!currentEvent?.id) {
      toast.error('Please select an event first');
      return;
    }

    try {
      await api.post(`/events/${currentEvent.id}/team-members`, {
        master_team_member_id: member.id,
        name: member.name,
        email: member.email || null,
        phone: member.phone,
        password: null
      });
      toast.success(`${member.name} added to the current event`);
      fetchTeamMembers();
      fetchMasterTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add team member to event');
    }
  };

  const selectedMasterTeamMember = newTeamMember.master_team_member_id
    ? masterTeamMembers.find((member) => member.id === newTeamMember.master_team_member_id)
    : null;

  const currentEventTeamMembers = teamMembers.filter((member) => !member.is_deleted);
  const assignedMasterMemberIds = new Set(
    currentEventTeamMembers
      .map((member) => member.master_team_member_id)
      .filter(Boolean)
  );
  const assignedMasterUserIds = new Set(
    currentEventTeamMembers
      .map((member) => member.user_id)
      .filter(Boolean)
  );
  const currentEventCount = currentEventTeamMembers.length;
  const masterTeamMemberCount = masterTeamMembers.length;

  const matchesTeamMemberSearch = (member) => {
    const query = teamMemberSearch.trim().toLowerCase();
    if (!query) return true;
    return [member.name, member.email, member.phone]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  };

  const matchesTeamMemberFilter = (member, source) => {
    const assigned = source === 'event' || isMasterMemberInCurrentEvent(member);
    if (teamMemberFilter === 'active') return member.is_active;
    if (teamMemberFilter === 'disabled') return !member.is_active;
    if (teamMemberFilter === 'assigned') return assigned;
    if (teamMemberFilter === 'unassigned') return !assigned;
    return true;
  };

  const filteredCurrentEventTeamMembers = currentEventTeamMembers.filter(
    (member) => matchesTeamMemberSearch(member) && matchesTeamMemberFilter(member, 'event')
  );

  const filteredMasterTeamMembers = masterTeamMembers.filter(
    (member) => matchesTeamMemberSearch(member) && matchesTeamMemberFilter(member, 'master')
  );

  const activeTeamMembersForCurrentTab = teamMembersTab === 'event'
    ? filteredCurrentEventTeamMembers
    : filteredMasterTeamMembers;

  const resetAddTeamMemberForms = () => {
    setNewTeamMember({ master_team_member_id: '', name: '', email: '', phone: '', password: '' });
    setNewMasterTeamMember({ name: '', email: '', phone: '', password: '' });
  };

  const openAddTeamMemberDialog = () => {
    resetAddTeamMemberForms();
    setAddTeamMemberDialogOpen(true);
  };

  const openBulkUploadDialog = () => {
    setBulkUploadResult(null);
    setSelectedBulkTeamMembersFile(null);
    setBulkUploadDialogOpen(true);
  };

  const handleRemoveTeamMember = async (teamMemberId) => {
    if (!confirm('Remove this team member from the event?')) return;
    try {
      await api.delete(`/events/${currentEvent.id}/team-members/${teamMemberId}`);
      toast.success('Team member removed');
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-1">
          <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="settings-title">Settings</h1>
          <InfoTip content="Manage your account and preferences." label="About settings" />
        </div>

        {/* Profile Info */}
        <Card className="border-border/70 shadow-sm">
          <CardHeader className="px-5 py-4">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Information
              </CardTitle>
              <Badge className="w-fit bg-wedding-sage">{user?.role?.replace('_', ' ')}</Badge>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            <form onSubmit={handleSaveProfile} className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_220px_auto] md:items-end">
              <div className="space-y-2">
                <Label htmlFor="profile-name">Name</Label>
                <Input
                  id="profile-name"
                  value={profileName}
                  onChange={(event) => setProfileName(event.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-email">Email</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={profileEmail}
                  onChange={(event) => setProfileEmail(event.target.value)}
                  placeholder="Not set"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-phone">Phone</Label>
                <Input
                  id="profile-phone"
                  value={user?.phone || 'Not set'}
                  disabled
                  className="bg-muted/40"
                />
              </div>
              <Button
                type="submit"
                className="bg-wedding-sage hover:bg-wedding-sage/90"
                disabled={savingProfile}
              >
                {savingProfile ? (
                  <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Save
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-border/70 shadow-sm">
          <CardHeader className="px-5 py-4">
            <CardTitle className="flex items-center gap-2">
              <LockKey className="h-5 w-5" />
              Password Login
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            {user?.has_password ? (
              <p className="text-sm text-muted-foreground">
                Password login is enabled for your account. OTP remains the default login method.
              </p>
            ) : (
              <form onSubmit={handleCreatePassword} className="grid gap-3 sm:grid-cols-2 sm:items-end">
                <div className="space-y-2">
                  <Label htmlFor="new-account-password">Create Password</Label>
                  <Input
                    id="new-account-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    placeholder="At least 6 characters"
                    minLength={6}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-account-password">Confirm Password</Label>
                  <Input
                    id="confirm-account-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="Enter it again"
                    minLength={6}
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground sm:col-span-2">
                  Optional: create a password if you want an alternative to OTP login.
                </p>
                <Button
                  type="submit"
                  className="w-fit bg-wedding-sage hover:bg-wedding-sage/90 sm:col-span-2"
                  disabled={savingPassword}
                >
                  {savingPassword ? (
                    <SpinnerGap className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <LockKey className="mr-2 h-4 w-4" />
                  )}
                  Create Password
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Company Branding - Only for Event Managers */}
        {isEventManager && (
          <>
            <Card className="border-border/70 shadow-sm">
              <CardHeader className="px-5 py-4">
                <CardTitle className="flex items-center gap-2">
                  <Buildings className="w-5 h-5" />
                  Company Branding
                  <InfoTip content="Your company branding appears on event dashboards for your guests." label="About company branding" />
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <form onSubmit={handleSaveBranding} className="space-y-4">
                {/* Logo Upload */}
                <div className="space-y-3">
                  <Label>Company Logo</Label>
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-muted/30 cursor-pointer hover:border-wedding-sage/50 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {previewLogo ? (
                        <img 
                          src={previewLogo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${previewLogo}` : previewLogo} 
                          alt="Company logo" 
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoSelect}
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {previewLogo ? 'Change Logo' : 'Upload Logo'}
                      </Button>
                      {previewLogo && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={handleRemoveLogo}
                        >
                          <Trash className="w-4 h-4 mr-2" />
                          Remove
                        </Button>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Recommended: Square image, PNG or JPG, max 2MB
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Company Name */}
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Your Company Name"
                    data-testid="company-name-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    This will appear as &quot;Managed by [Your Company]&quot; on guest dashboards
                  </p>
                </div>

                {/* Company Website */}
                <div className="space-y-2">
                  <Label htmlFor="company_website">Company Website</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="company_website"
                      value={companyWebsite}
                      onChange={(e) => setCompanyWebsite(e.target.value)}
                      placeholder="https://yourcompany.com"
                      className="pl-10"
                      data-testid="company-website-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Clicking your company name will open this website
                  </p>
                </div>

                {/* Preview */}
                {(companyName || previewLogo) && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <Label>Preview</Label>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <p className="text-sm text-muted-foreground mb-2">How it will appear on dashboards:</p>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Managed by</span>
                          {previewLogo && (
                            <img 
                              src={previewLogo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${previewLogo}` : previewLogo}
                              alt="Preview" 
                              className="w-5 h-5 object-contain"
                            />
                          )}
                          <span className="font-medium text-wedding-sage">
                            {companyName || 'Your Company'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button 
                    type="submit" 
                    className="bg-wedding-sage hover:bg-wedding-sage/90"
                    disabled={loading}
                    data-testid="save-branding-btn"
                  >
                    {loading ? (
                      <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Save Branding
                  </Button>
                </div>
                </form>
              </CardContent>
            </Card>

            <Card className="border-border/70 shadow-sm">
              <CardHeader className="px-5 py-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center gap-1">
                    <CardTitle>Team members</CardTitle>
                    <InfoTip content="Review staff, assign reusable members, and keep logins tidy." label="About team members" />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={openBulkUploadDialog}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Bulk Upload
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="bg-wedding-sage hover:bg-wedding-sage/90"
                      onClick={openAddTeamMemberDialog}
                      disabled={teamMembersTab === 'event' && !currentEvent?.id}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Team Member
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <Tabs value={teamMembersTab} onValueChange={setTeamMembersTab} className="space-y-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <TabsList className="grid w-full grid-cols-2 lg:w-auto">
                      <TabsTrigger value="event" className="gap-2">
                        Current Event
                        <Badge variant="secondary" className="h-5 px-1.5 text-[0.6875rem]">
                          {currentEventCount}
                        </Badge>
                      </TabsTrigger>
                      <TabsTrigger value="master" className="gap-2">
                        Master List
                        <Badge variant="secondary" className="h-5 px-1.5 text-[0.6875rem]">
                          {masterTeamMemberCount}
                        </Badge>
                      </TabsTrigger>
                    </TabsList>

                    <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_160px] lg:w-[520px]">
                      <div className="relative">
                        <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          value={teamMemberSearch}
                          onChange={(event) => setTeamMemberSearch(event.target.value)}
                          placeholder="Search name, email, phone"
                          className="h-9 pl-9"
                        />
                      </div>
                      <Select value={teamMemberFilter} onValueChange={setTeamMemberFilter}>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="assigned">Assigned</SelectItem>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          <SelectItem value="disabled">Disabled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <TabsContent value="event" className="mt-0">
                    {!currentEvent?.id ? (
                      <div className="rounded-lg border border-dashed p-5 text-sm text-muted-foreground">
                        Select an event to manage its team.
                      </div>
                    ) : teamMembersLoading ? (
                      <div className="rounded-lg border p-5 text-sm text-muted-foreground">Loading team members...</div>
                    ) : filteredCurrentEventTeamMembers.length > 0 ? (
                      <div className="overflow-hidden rounded-lg border">
                        {filteredCurrentEventTeamMembers.map((member) => (
                          <div key={member.id} className="grid gap-3 border-b px-4 py-3 last:border-b-0 md:grid-cols-[minmax(190px,1.2fr)_minmax(160px,1fr)_auto] md:items-center">
                            <div className="min-w-0">
                              <p className="truncate font-medium text-wedding-charcoal">{member.name}</p>
                              <p className="truncate text-sm text-muted-foreground">{member.email || 'No email'}</p>
                              {member.phone && <p className="truncate text-xs text-muted-foreground">{member.phone}</p>}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Badge className={member.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                                {member.is_active ? 'Active' : 'Disabled'}
                              </Badge>
                              <Badge variant="outline">Assigned to current event</Badge>
                            </div>
                            <div className="flex flex-wrap gap-2 md:justify-end">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleTeamMemberStatus(member, 'event')}
                              >
                                <Power className="w-4 h-4 mr-2" />
                                {member.is_active ? 'Disable' : 'Enable'}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleRemoveTeamMember(member.id)}
                              >
                                <Trash className="w-4 h-4 mr-2" />
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed p-5 text-sm text-muted-foreground">
                        {activeTeamMembersForCurrentTab.length === 0 && (teamMemberSearch || teamMemberFilter !== 'all')
                          ? 'No team members match this search or filter.'
                          : 'No team members added for this event yet.'}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="master" className="mt-0">
                    {masterTeamMembersLoading ? (
                      <div className="rounded-lg border p-5 text-sm text-muted-foreground">Loading reusable team members...</div>
                    ) : filteredMasterTeamMembers.length > 0 ? (
                      <div className="overflow-hidden rounded-lg border">
                        {filteredMasterTeamMembers.map((member) => {
                          const assigned = isMasterMemberInCurrentEvent(member);
                          return (
                            <div key={member.id} className="grid gap-3 border-b px-4 py-3 last:border-b-0 md:grid-cols-[minmax(190px,1.2fr)_minmax(180px,1fr)_auto] md:items-center">
                              <div className="min-w-0">
                                <p className="truncate font-medium text-wedding-charcoal">{member.name}</p>
                                <p className="truncate text-sm text-muted-foreground">{member.email || 'No email'}</p>
                                {member.phone && <p className="truncate text-xs text-muted-foreground">{member.phone}</p>}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Badge className={member.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                                  {member.is_active ? 'Active' : 'Disabled'}
                                </Badge>
                                <Badge variant="outline">
                                  {assigned ? 'Assigned to current event' : 'Unassigned'}
                                </Badge>
                              </div>
                              <div className="flex flex-wrap gap-2 md:justify-end">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleAddMasterTeamMemberToCurrentEvent(member)}
                                  disabled={assigned || !currentEvent?.id}
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Assign
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenEditMasterTeamMember(member)}
                                >
                                  <PencilSimple className="w-4 h-4 mr-2" />
                                  Edit
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleTeamMemberStatus(member, 'master')}
                                >
                                  <Power className="w-4 h-4 mr-2" />
                                  {member.is_active ? 'Disable' : 'Enable'}
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed p-5 text-sm text-muted-foreground">
                        {activeTeamMembersForCurrentTab.length === 0 && (teamMemberSearch || teamMemberFilter !== 'all')
                          ? 'No team members match this search or filter.'
                          : 'No reusable team members yet. Add one manually or upload a spreadsheet to start building the master list.'}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            <Dialog
              open={addTeamMemberDialogOpen}
              onOpenChange={(open) => {
                setAddTeamMemberDialogOpen(open);
                if (!open) resetAddTeamMemberForms();
              }}
            >
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Team Member</DialogTitle>
                  <DialogDescription>
                    {teamMembersTab === 'event'
                      ? 'Add a member to the current event or reuse a saved login.'
                      : 'Save a reusable staff login in the master list.'}
                  </DialogDescription>
                </DialogHeader>

                {teamMembersTab === 'event' ? (
                  <form onSubmit={handleAddTeamMember} className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="saved-team-member">Add from master list</Label>
                      <Select
                        value={newTeamMember.master_team_member_id || 'new'}
                        onValueChange={(value) => handleSelectSavedTeamMember(value === 'new' ? '' : value)}
                      >
                        <SelectTrigger id="saved-team-member">
                          <SelectValue placeholder="Start a new login or reuse an existing one" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Create new team member</SelectItem>
                          {masterTeamMembers.map((member) => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.name}{member.email ? ` - ${member.email}` : ''}{isMasterMemberInCurrentEvent(member) ? ' (already added)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedMasterTeamMember && (
                        <p className="text-xs text-muted-foreground">
                          Reusing <span className="font-medium">{selectedMasterTeamMember.name}</span>. Their login will be linked to this event.
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="team-member-name">Name</Label>
                      <Input
                        id="team-member-name"
                        value={newTeamMember.name}
                        onChange={(e) => setNewTeamMember({ ...newTeamMember, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="team-member-email">Email</Label>
                      <Input
                        id="team-member-email"
                        type="email"
                        value={newTeamMember.email}
                        onChange={(e) => setNewTeamMember({ ...newTeamMember, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="team-member-phone">Phone</Label>
                      <Input
                        id="team-member-phone"
                        value={newTeamMember.phone}
                        onChange={(e) => setNewTeamMember({ ...newTeamMember, phone: e.target.value })}
                        required
                      />
                    </div>
                    {!newTeamMember.master_team_member_id && (
                      <div className="space-y-2">
                        <Label htmlFor="team-member-password">Temporary Password</Label>
                        <Input
                          id="team-member-password"
                          type="password"
                          value={newTeamMember.password}
                          onChange={(e) => setNewTeamMember({ ...newTeamMember, password: e.target.value })}
                          required
                        />
                      </div>
                    )}
                    {newTeamMember.master_team_member_id && (
                      <div className="md:col-span-2 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                        This will reuse an existing login. No new password is needed.
                      </div>
                    )}
                    <DialogFooter className="md:col-span-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setAddTeamMemberDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-wedding-sage hover:bg-wedding-sage/90"
                        disabled={savingTeamMember}
                      >
                        {savingTeamMember ? (
                          <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Add Team Member
                      </Button>
                    </DialogFooter>
                  </form>
                ) : (
                  <form onSubmit={handleAddMasterTeamMember} className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="master-team-member-name">Name</Label>
                      <Input
                        id="master-team-member-name"
                        value={newMasterTeamMember.name}
                        onChange={(e) => setNewMasterTeamMember({ ...newMasterTeamMember, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="master-team-member-email">Email</Label>
                      <Input
                        id="master-team-member-email"
                        type="email"
                        value={newMasterTeamMember.email}
                        onChange={(e) => setNewMasterTeamMember({ ...newMasterTeamMember, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="master-team-member-phone">Phone</Label>
                      <Input
                        id="master-team-member-phone"
                        value={newMasterTeamMember.phone}
                        onChange={(e) => setNewMasterTeamMember({ ...newMasterTeamMember, phone: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="master-team-member-password">Temporary Password</Label>
                      <Input
                        id="master-team-member-password"
                        type="password"
                        value={newMasterTeamMember.password}
                        onChange={(e) => setNewMasterTeamMember({ ...newMasterTeamMember, password: e.target.value })}
                        required
                      />
                    </div>
                    <DialogFooter className="md:col-span-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setAddTeamMemberDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-wedding-sage hover:bg-wedding-sage/90"
                        disabled={savingMasterTeamMemberCreate}
                      >
                        {savingMasterTeamMemberCreate ? (
                          <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Add Manually
                      </Button>
                    </DialogFooter>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            <Dialog
              open={bulkUploadDialogOpen}
              onOpenChange={(open) => {
                setBulkUploadDialogOpen(open);
                if (!open) {
                  setSelectedBulkTeamMembersFile(null);
                  setBulkUploadResult(null);
                }
              }}
            >
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Bulk Upload Team Members</DialogTitle>
                  <DialogDescription>
                    Import reusable staff into the master list from an XLSX file.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <input
                    ref={bulkTeamMembersFileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleBulkTeamMemberFileSelect}
                  />

                  <div className="grid gap-3 rounded-lg border bg-muted/20 p-4 sm:grid-cols-3">
                    <div>
                      <Badge variant="outline">1</Badge>
                      <p className="mt-2 text-sm font-medium">Template</p>
                    </div>
                    <div>
                      <Badge variant="outline">2</Badge>
                      <p className="mt-2 text-sm font-medium">Choose file</p>
                    </div>
                    <div>
                      <Badge variant="outline">3</Badge>
                      <p className="mt-2 text-sm font-medium">Confirm import</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={downloadMasterTeamTemplate}
                      disabled={downloadingMasterTemplate}
                    >
                      {downloadingMasterTemplate ? (
                        <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Download Template
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => bulkTeamMembersFileInputRef.current?.click()}
                      disabled={uploadingMasterTeamMembers}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Choose File
                    </Button>
                  </div>

                  {selectedBulkTeamMembersFile && (
                    <div className="rounded-lg border p-3 text-sm">
                      <p className="font-medium text-wedding-charcoal">{selectedBulkTeamMembersFile.name}</p>
                      <p className="text-muted-foreground">
                        {Math.max(1, Math.round(selectedBulkTeamMembersFile.size / 1024))} KB ready to import
                      </p>
                    </div>
                  )}

                  {bulkUploadResult && (
                    <div className="space-y-3 rounded-lg border p-3 text-sm">
                      <div className="flex flex-wrap gap-2">
                        <Badge className="bg-green-100 text-green-700">Added {bulkUploadResult.added || 0}</Badge>
                        <Badge className="bg-blue-100 text-blue-700">Updated {bulkUploadResult.updated || 0}</Badge>
                        <Badge className="bg-gray-100 text-gray-700">Skipped {bulkUploadResult.skipped_count || 0}</Badge>
                      </div>
                      {bulkUploadResult.skipped?.length > 0 && (
                        <div className="max-h-36 space-y-2 overflow-y-auto">
                          {bulkUploadResult.skipped.map((row) => (
                            <div key={`${row.row}-${row.name}`} className="rounded border bg-muted/20 p-2">
                              Row {row.row}: {row.reason}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setBulkUploadDialogOpen(false)}
                  >
                    {bulkUploadResult ? 'Done' : 'Cancel'}
                  </Button>
                  <Button
                    type="button"
                    className="bg-wedding-sage hover:bg-wedding-sage/90"
                    disabled={!selectedBulkTeamMembersFile || uploadingMasterTeamMembers}
                    onClick={handleBulkMasterTeamMemberUpload}
                  >
                    {uploadingMasterTeamMembers ? (
                      <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Import File
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog
              open={Boolean(editingMasterTeamMember)}
              onOpenChange={(open) => {
                if (!open) {
                  setEditingMasterTeamMember(null);
                }
              }}
            >
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Team Member</DialogTitle>
                  <DialogDescription>
                    Update the saved contact details used across future events.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSaveMasterTeamMember} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="edit-team-member-name">Name</Label>
                    <Input
                      id="edit-team-member-name"
                      value={editingMasterTeamMemberForm.name}
                      onChange={(e) => setEditingMasterTeamMemberForm({
                        ...editingMasterTeamMemberForm,
                        name: e.target.value
                      })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-team-member-email">Email</Label>
                    <Input
                      id="edit-team-member-email"
                      type="email"
                      value={editingMasterTeamMemberForm.email}
                      onChange={(e) => setEditingMasterTeamMemberForm({
                        ...editingMasterTeamMemberForm,
                        email: e.target.value
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-team-member-phone">Phone</Label>
                    <Input
                      id="edit-team-member-phone"
                      value={editingMasterTeamMemberForm.phone}
                      onChange={(e) => setEditingMasterTeamMemberForm({
                        ...editingMasterTeamMemberForm,
                        phone: e.target.value
                      })}
                      required
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditingMasterTeamMember(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-wedding-sage hover:bg-wedding-sage/90"
                      disabled={savingMasterTeamMember}
                    >
                      {savingMasterTeamMember ? (
                        <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* Non-event manager message */}
        {!isEventManager && (
          <Card className="bg-muted/30">
            <CardContent className="py-8 text-center">
              <Buildings className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                Company branding settings are only available for Event Managers.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
